import * as React from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { buttonVariants } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/**
 * ConfirmDialog · reemplazo de window.confirm() nativo.
 *
 * El window.confirm() del navegador se suprime silenciosamente cuando la app
 * corre embebida en un iframe (preview de Emergent), rompiendo flujos
 * destructivos importantes. Este wrapper usa el AlertDialog de shadcn/ui,
 * consistente con el diseño Stripe-like del producto.
 *
 * Uso:
 *   const confirm = useConfirm();
 *   if (!await confirm({ title, description, confirmLabel, variant })) return;
 *   ... acción destructiva ...
 */

const ConfirmContext = React.createContext(null);

export function ConfirmDialogProvider({ children }) {
  const [state, setState] = React.useState({ open: false });
  const resolverRef = React.useRef(null);

  const confirm = React.useCallback((opts) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setState({
        open: true,
        title: opts.title || '¿Estás seguro?',
        description: opts.description || 'Esta acción no se puede deshacer.',
        confirmLabel: opts.confirmLabel || 'Confirmar',
        cancelLabel: opts.cancelLabel || 'Cancelar',
        variant: opts.variant || 'default',
      });
    });
  }, []);

  const handleConfirm = () => {
    if (resolverRef.current) resolverRef.current(true);
    resolverRef.current = null;
    setState((s) => ({ ...s, open: false }));
  };

  const handleCancel = () => {
    if (resolverRef.current) resolverRef.current(false);
    resolverRef.current = null;
    setState((s) => ({ ...s, open: false }));
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <AlertDialog
        open={state.open}
        onOpenChange={(open) => { if (!open) handleCancel(); }}
      >
        <AlertDialogContent data-testid="confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{state.title}</AlertDialogTitle>
            {state.description && (
              <AlertDialogDescription>{state.description}</AlertDialogDescription>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={handleCancel}
              data-testid="confirm-dialog-cancel"
            >
              {state.cancelLabel}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirm}
              data-testid="confirm-dialog-confirm"
              className={cn(
                state.variant === 'destructive' &&
                  buttonVariants({ variant: 'destructive' })
              )}
            >
              {state.confirmLabel}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = React.useContext(ConfirmContext);
  if (!ctx) {
    throw new Error('useConfirm debe usarse dentro de <ConfirmDialogProvider>');
  }
  return ctx;
}
